// Usage: calculateShipping(postcode, weight, length, width, height)
// Data file: freightData.json
// Fuel levy from: ../data/levy.json (imile key)

import freightData from './freightData.json';
import levy from '../data/levy.json';

// ========== CONSTANTS ==========
const FUEL_LEVY_RATE = levy.imile; // iMile 燃油费率 from levy.json
const CUBIC_CONVERSION = 4000;     // cm³ to kg conversion (volumetric)

// ========== SIZE/WEIGHT LIMITS (from iMile T&C) ==========
const MAX_WEIGHT = 20;             // kg
const MAX_SINGLE_SIDE = 100;       // cm - 单边不超过 100cm
const MAX_SUM_DIMENSIONS = 120;    // cm - 三边之和不超过 120cm

// ========== HELPERS ==========

function getZone(postcode, zonesMap) {
  const pc = String(postcode).trim().padStart(4, '0');
  return zonesMap[pc] || zonesMap[String(parseInt(pc, 10))] || null;
}

/**
 * 根据计费重量获取对应的价格
 * Weight brackets: 0-0.25, 0.25-0.5, 0.5-1, 1-2, 2-3, 3-5, 5-10, 10-15, 15-20
 */
function getPriceForWeight(zonePricing, chargeableWeight) {
  if (!zonePricing) return null;

  if (chargeableWeight <= 0.25) return zonePricing['250gm'];
  if (chargeableWeight <= 0.5)  return zonePricing['500gm'];
  if (chargeableWeight <= 1)    return zonePricing['1kg'];
  if (chargeableWeight <= 2)    return zonePricing['2kg'];
  if (chargeableWeight <= 3)    return zonePricing['3kg'];
  if (chargeableWeight <= 5)    return zonePricing['5kg'];
  if (chargeableWeight <= 10)   return zonePricing['10kg'];
  if (chargeableWeight <= 15)   return zonePricing['15kg'];
  if (chargeableWeight <= 20)   return zonePricing['20kg'];

  return null; // Over 20kg
}

/**
 * 验证尺寸和重量限制 (iMile T&C)
 * - 单边 ≤ 100cm
 * - 三边之和 ≤ 120cm
 * - 重量 ≤ 20kg
 */
function validatePackage(weight, length, width, height) {
  if (weight > MAX_WEIGHT) {
    return `Weight ${weight}kg exceeds maximum ${MAX_WEIGHT}kg`;
  }

  const maxSide = Math.max(length, width, height);
  if (maxSide > MAX_SINGLE_SIDE) {
    return `Max side ${maxSide}cm exceeds ${MAX_SINGLE_SIDE}cm limit`;
  }

  const sumDims = length + width + height;
  if (sumDims > MAX_SUM_DIMENSIONS) {
    return `Sum of dimensions ${sumDims}cm exceeds ${MAX_SUM_DIMENSIONS}cm limit`;
  }

  return null;
}

// ========== CALCULATION ==========

function calculateFreight(postcode, weight, length, width, height) {
  const { zones, pricing } = freightData;

  const result = {
    postcode: String(postcode).trim(),
    zone: null,
    zoneName: null,
    actualWeight: 0,
    cubicWeight: 0,
    chargeableWeight: 0,
    basePrice: 0,
    fuelLevyRate: FUEL_LEVY_RATE,
    fuelLevy: 0,
    totalFreight: 0,
    error: null,
  };

  // 1. Parse inputs
  const l = parseFloat(length) || 0;
  const w = parseFloat(width) || 0;
  const h = parseFloat(height) || 0;
  const aw = parseFloat(weight) || 0;

  result.actualWeight = aw;

  // 2. Validate dimensions and weight
  const validationError = validatePackage(aw, l, w, h);
  if (validationError) {
    result.error = validationError;
    return result;
  }

  // 3. Postcode -> Zone
  const zoneCode = getZone(postcode, zones);
  if (!zoneCode) {
    result.error = `No zone found for postcode ${postcode}`;
    return result;
  }
  result.zone = zoneCode;

  // 4. Get zone pricing
  const zonePricing = pricing[zoneCode];
  if (!zonePricing) {
    result.error = `No pricing found for zone ${zoneCode}`;
    return result;
  }
  result.zoneName = zonePricing.name;

  // 5. Calculate chargeable weight
  //    - Weight ≤ 3kg: actual weight only
  //    - Weight > 3kg: MAX(actual, volumetric)
  const cubicWeight = (l * w * h) / CUBIC_CONVERSION;
  result.cubicWeight = +cubicWeight.toFixed(5);

  let chargeableWeight;
  if (aw <= 3) {
    chargeableWeight = aw;
  } else {
    chargeableWeight = Math.max(aw, cubicWeight);
  }
  result.chargeableWeight = +chargeableWeight.toFixed(5);

  // 6. Get price for weight bracket
  const basePrice = getPriceForWeight(zonePricing, chargeableWeight);
  if (basePrice === null) {
    result.error = `Weight ${chargeableWeight}kg out of range for zone ${zoneCode}`;
    return result;
  }
  result.basePrice = +basePrice.toFixed(5);

  // 7. Apply fuel levy: price * (1 + fuelRate)
  const fuelLevy = basePrice * FUEL_LEVY_RATE;
  result.fuelLevy = +fuelLevy.toFixed(5);

  const totalFreight = basePrice * (1 + FUEL_LEVY_RATE);
  result.totalFreight = +totalFreight.toFixed(5);

  return result;
}

// 适配器函数，匹配其他快递公司的接口: (postcode, weight, length, [width], [height])
export const calculateShipping = (postcode, weight, length, width = 30, height = 30) => {
  const result = calculateFreight(postcode, weight, length, width, height);

  if (result.error) {
    return 999999;
  }

  return result.totalFreight;
};

// 导出详细计算函数和辅助函数供需要的地方使用
export { calculateFreight, getZone, getPriceForWeight };

/*
使用说明
1. calculateShipping(postcode, weight, length, [width], [height]) - 简化接口，返回运费总额
2. calculateFreight(postcode, weight, length, width, height) - 详细接口，返回完整计算信息
3. 参数说明:
   - postcode: 邮编 (必填)
   - weight: 实际重量 (kg)
   - length: 长度 (cm)
   - width: 宽度 (cm, 默认 30)
   - height: 高度 (cm, 默认 30)
4. 尺寸/重量限制 (超出返回错误):
   - 重量 ≤ 20kg
   - 单边 ≤ 100cm
   - 三边之和 (L+W+H) ≤ 120cm
5. 计费重量规则:
   - 重量 ≤ 3kg: 使用实际重量
   - 重量 > 3kg: MAX(实际重量, 体积重量)
6. 体积重量计算: (长 × 宽 × 高) ÷ 4000
7. 运费公式: 对应重量档位价格 × (1 + 燃油费率)
8. 燃油费率: 从 levy.json 的 imile 字段读取 (6.1%)
9. 重量档位: 0.25kg, 0.5kg, 1kg, 2kg, 3kg, 5kg, 10kg, 15kg, 20kg
10. 所有计算结果保留 5 位小数
*/
